import {AbstractInteractionMode, DrawStyleUtil} from "./abstract-interaction-mode";
import {Layer, LayerGeometry} from "../../document/model/layer";
import {MapEvent} from "../../document/model/map";
import {Feature, Geometry, Point, Polygon, Position} from "geojson";
import {FeatureUtil} from "../feature";
import store from "../../store"
import {EditorMode} from "./editor-mode";
import {defineAsyncComponent} from "vue";
import {GeoreferencerSimpleSelect} from "../interaction/georeferencer/georeferencer-simple-select";
import {
  GeoreferencerBoundsTransformTranslate
} from "../interaction/georeferencer/georeferencer-bounds-transform-translate";
import {GeoreferencerBoundsTransformScale} from "../interaction/georeferencer/georeferencer-bounds-transform-scale";
import {GeoreferencerDrawPoint} from "../interaction/georeferencer/georeferencer-draw-point";
import {
  GeoreferencerPointTransformTranslate
} from "../interaction/georeferencer/georeferencer-point-transform-translate";
import {GeometryUtil} from "../geometry";
import {GeoreferencerDeleteSelect} from "../interaction/georeferencer/georeferencer-delete-select";

let GeoreferenceModal = defineAsyncComponent(() => import("../../../components/map/GeoreferenceModal.vue"));

export class GeoReferencerMode extends AbstractInteractionMode {
  static identifier: string = "georeferencer";
  static title: string = "Georeferențiere";
  static icon: string = "/icons/toolbox/georeference.svg";
  static hidden: boolean = true;

  static drawStyles = DrawStyleUtil.drawStyle({
    "line-color": '#D20C0C',
    "line-dasharray": [0.2, 2],
    "fill-color": '#D20C0C',
    "fill-opacity": 0,
    "border-color": '#D20C0C',
  });
  static defaultInteraction = 'draw_point';
  static interactions = {
    'select': GeoreferencerSimpleSelect.definition({selectionAllowed: true, changeset: 'select'}),

    'draw_point': GeoreferencerDrawPoint.definition(),
    'point_transform_translate': GeoreferencerPointTransformTranslate.definition({
      selectionAllowed: true,
      changeset: 'point'
    }),
    'delete_select': GeoreferencerDeleteSelect.definition({
      selectionAllowed: true,
      multiSelection: true,
      changeset: 'point_delete'
    })
  }

  static allowChanges: boolean = true;
  static allowPriorityContextMenu: boolean = false;

  onInit() {
    this.controller.replaceLayer(this.layer, this.layer);
    EditorMode.prototype.setupLayerGeometryRemoveHandler.apply(this, [arguments]);

    this._mapInstance.on(MapEvent.PinCreated, (e) => {
      this.controller.reloadLayer(this.layer);
      if (e.action === 'update')
        this.controller.interaction.mode.updateCursor();
      else
        this.controller.interaction.mode.changeInteraction(this.controller.interaction.mode.currentInteractionIdentifier)


    });

    this._mapInstance.on(MapEvent.PinSelected, (e) => {
      if (e.feature.geometryId) {
        let geometry = JSON.parse(JSON.stringify(e.feature.geometry));
        let layerGeometry: LayerGeometry = {
          documentId: e.feature.documentId,
          geometryId: e.feature.geometryId,
          document: this.layer,
          attributes: {...e.feature.attributes},
          geometry: {
            id: e.feature.geometryId,
            type: "Feature",
            geometry: geometry,
            properties: {...e.feature.attributes},
          },
        };
        this.controller.interaction.focusSingle(layerGeometry, false);
      }
    });
  }

  onDestroy() {
    this.controller.replaceLayer(this.layer, this.layer);
    super.onDestroy();
  }

  getBoundingPolygon(): LayerGeometry {
    return {
      geometryId: 'bounding-polygon',
      geometry: {
        type: 'Feature',
        id: 'bounding-polygon',
        geometry: {
          type: 'Polygon',
          coordinates: [[...this.getLayer().properties.boundingPolygon, this.getLayer().properties.boundingPolygon[0]]]
        },
        properties: {
          id: 'bounding-polygon',
        }
      } as Feature<Polygon>,
      documentId: this.getLayer().documentId,
      document: this.getLayer(),
      attributes: {},
      originalGeometry: undefined
    }
  }

  async getLayerGeometries(geometries: any[]): Promise<LayerGeometry[]> {
    let fetchedGeometries = await super.getLayerGeometries(geometries);
    if (this.controller.configuration.interactionOptions?.georeferenceViewMode === 'canvas') {
      fetchedGeometries.forEach(geometry => {
        geometry.geometry = FeatureUtil.reProjectToCanvas(
          [geometry.geometry as Feature<Point>],
          this.layer.properties.boundingPolygon,
          this.layer.properties.size
        )[0]
      })
    }
    return fetchedGeometries;
  }

  async onLayerGeometriesUpdate(layerGeometry: LayerGeometry, feature: Feature<Point | Polygon>) {

    switch (feature.geometry.type) {
      case 'Polygon':
        this.getLayer().properties.boundingPolygon = (feature.geometry.coordinates[0] as Position[]).slice(0, 4);
        this.persistLayer().then(() => this.reset());
        break;
      default:
        let referenceViewMode = this.controller.configuration.interactionOptions.georeferenceViewMode;
        let relativePosition = GeometryUtil.relativePosition(
          feature.geometry.coordinates as Position,
          this.layer.properties.boundingPolygon,
          this.layer.properties.size
        );
        if (referenceViewMode === 'canvas') {

          feature.properties.offsetX = relativePosition.x;
          feature.properties.offsetY = relativePosition.y;

          feature.geometry.coordinates = (layerGeometry.geometry.geometry as Point).coordinates;

          //todo: original coordinates get overwritten
        }
        layerGeometry = await this.saveLayerGeometry(layerGeometry, feature, layerGeometry.document);

        this.controller.reloadLayer(layerGeometry.document);

        this._mapInstance.fire(
          MapEvent.LayerGeometryUpdated,
          {
            layer: layerGeometry.document,
            layerGeometry: layerGeometry,
          }
        )
    }
  }

  private async saveLayerGeometry(layerGeometry: LayerGeometry, feature: Feature<Point | Polygon>, activeModeLayer: Layer | undefined) {
    return EditorMode.prototype.saveLayerGeometry.apply(this, [layerGeometry, feature, activeModeLayer]);
  }

  async onLayerGeometriesCreate(features: Feature[]) {
    features.forEach((feature: Feature<Point>) => {
      feature.properties.offsetX = '0';
      feature.properties.offsetY = '0';
      let relativePosition = GeometryUtil.relativePosition(
        feature.geometry.coordinates as Position,
        this.layer.properties.boundingPolygon,
        this.layer.properties.size
      );
      feature.properties.offsetX = relativePosition.x;
      feature.properties.offsetY = relativePosition.y;
    });
    await EditorMode.prototype.onLayerGeometriesCreate.apply(this, [features]);
  }

  getGlobalActions(drawMode, point: mapboxgl.Point | undefined = undefined): any[] {
    let actions = [];

    if (!this.controller.configuration.interactionOptions?.slim) {
      actions.push(
        {
          label: 'Split view',
          icon: '/icons/toolbox/compare.svg',
          command: () => {
            this._mapInstance.fire(MapEvent.MapGeoReferenceCompare)
          },
          pos: {position: 'bottom'}
        }
      )
    }


    return actions;
  }

  getActionOptions(interactionIdentifier) {
    let options: any = [];
    if (["draw_polygon", "draw_point", "draw_line"].includes(interactionIdentifier))
      options.push(
        {
          label: "Vertex list",
          icon: "/icons/toolbox/list.svg",
          command: () => {
            if (this.controller.interaction.hasManualInputDialog()) {
              this.controller.interaction.hideManualInputDialog();
            } else {
              this.controller.interaction.showManualInputDialog();
            }
            this._mapInstance.fire(MapEvent.Tick);
          },
          active: this.controller.interaction.hasManualInputDialog(),
          pos: { position: 'bottom' }
        }
      );
    return options;
  }

  reset(): void {
    this.controller.replaceLayer(this.getLayer(), this.getLayer());
    this.getLayerGeometries(
      this.controller.interaction.getFocused().map(layerGeometry => layerGeometry.geometryId)
    )
      .then(layerGeometries => {
        layerGeometries.forEach(layerGeometry => {
          let existing = this.controller.interaction.getLayerGeometry(layerGeometry.geometryId);
          existing.geometry = layerGeometry.geometry;
        });
        this.controller.interaction.mode.resetInteraction();
        this._mapInstance.fire(MapEvent.Tick);
      })
  }

  /*private changeMapMode(mode: string) {
      this.controller.configuration.interactionOptions.georeferenceViewMode = mode;
      this.controller.replaceLayer(this.getLayer(), this.getLayer());
      if (['bounds_transform_translate', 'bounds_transform_scale'].indexOf(this.currentInteractionIdentifier) >= 0) {
          (this.currentInteraction as GeoreferencerAbstractBoundsTransformer).updateBoundingBox();
      }
      this._mapInstance.fire(MapEvent.Tick);
  }*/

  private persistLayer(): Promise<any> {
    return store
      .dispatch("editLayer", {
        query: {
          moduleId: this.controller.configuration.moduleId,
        },
        data: this.layer,
      })
      .then((response) => {
      })
      .catch((error) => {
        this.controller.configuration.toastProvider({
          severity: "error",
          summary: "Error",
          detail: error,
          life: 5000,
        });
      });
  }
}
